/**
 * Electron desktop integration hook.
 *
 * When the app is running inside the VACARS5 Electron shell, `window.electronAPI`
 * exists. This hook:
 *   - pushes the current session (bearer token + api base) into the main
 *     process so main can POST telemetry to the API from Node,
 *   - subscribes to sim status + telemetry events emitted by main,
 *   - tells main which live_id is currently active so telemetry is scoped.
 *
 * Outside Electron the hook returns { desktop: false } and does nothing.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";

const IS_DESKTOP = typeof window !== "undefined" && !!window.electronAPI;

export function useElectronSim(activeFlight) {
  const { user } = useAuth();
  const [status, setStatus] = useState({ connected: false, sim: null });
  const [telemetry, setTelemetry] = useState(null);

  // Push auth session down to main process so it can POST telemetry.
  useEffect(() => {
    if (!IS_DESKTOP) return;
    const token = localStorage.getItem("flyvac_token");
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    if (token && apiBase) {
      window.electronAPI.session.set({ token, apiBase, user });
    } else {
      window.electronAPI.session.clear();
    }
  }, [user]);

  // Tell main which flight is active so telemetry posting scopes correctly,
  // and what the overlay shows about it. Keyed on more than the id: the
  // server resolves gates and airport positions after the flight starts, and
  // keyed on the id alone the overlay never heard about either.
  const overlayKey = activeFlight
    ? [activeFlight.id, activeFlight.gateOrig, activeFlight.gateDest,
       activeFlight.orig_pos, activeFlight.dest_pos].map((v) => String(v ?? "")).join("|")
    : "";
  useEffect(() => {
    if (!IS_DESKTOP) return;
    window.electronAPI.acars.setActive(activeFlight || null);
  }, [overlayKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to sim events.
  useEffect(() => {
    if (!IS_DESKTOP) return;
    let mounted = true;
    window.electronAPI.sim.status().then((s) => mounted && setStatus(s));
    const offS = window.electronAPI.sim.onStatus((s) => setStatus(s));
    const offT = window.electronAPI.sim.onTelemetry((t) => setTelemetry(t));
    return () => { mounted = false; offS?.(); offT?.(); };
  }, []);

  return { desktop: IS_DESKTOP, status, telemetry };
}

export const isDesktop = () => IS_DESKTOP;

/**
 * "0.9.0 beta" in the desktop app, null in a browser. Every build used to say
 * 1.0.0 and the app showed no version at all, so neither a pilot nor support
 * could tell which build was running. 0.x is the beta series (2026-10-03).
 */
export function useAppVersion() {
  const [version, setVersion] = useState(null);
  useEffect(() => {
    if (!IS_DESKTOP || !window.electronAPI.appVersion) return;
    let live = true;
    window.electronAPI.appVersion().then((v) => live && setVersion(v)).catch(() => {});
    return () => { live = false; };
  }, []);
  if (!version) return null;
  return String(version).startsWith("0.") ? `${version} beta` : version;
}
